import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, Brain, Target, TrendingUp, UserCheck,
  ChevronDown, Check, Menu, X, Play
} from 'lucide-react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useCountryPrices } from '@/hooks/useCountryPrices';
import { CountrySheet } from '@/components/CountrySheet';
import { flagFor } from '@/utils/countryFlag';
import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Marquee from 'react-fast-marquee';
import { useTheme } from '@/contexts/ThemeContext';
import { isAndroid } from '@/utils/webviewDetect';
import { LandingAppCarousels, LandingPrograms, LandingTrackers } from '@/components/LandingShowcase';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.wnapp.id1780845607573&utm_source=bodymax_landing';

// ─── Design tokens ───
const BG = '#0A0A0A';
const SURFACE = '#141414';
const SURFACE_EL = '#1F1F1F';
const ACCENT = '#0096FC';
// Brand blue sampled from the programme artwork — used for every landing CTA.
const CTA_BLUE = '#0096FC';
// Lighter blue for the small labels and pricing tabs.
const LIGHT_BLUE = '#63B3FF';
const TEXT_SEC = '#A3A3A3';

// ─── Images (local assets) ───
const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/00509d42-1bd5-4d51-996e-7cadb42747be/images/d189df359d35e023d1cd6fd3971916c1ea67dbf043c847632f316b33118dc3f6.jpeg';
const HERO_IMG_FALLBACK = 'https://static.prod-images.emergentagent.com/jobs/00509d42-1bd5-4d51-996e-7cadb42747be/images/d189df359d35e023d1cd6fd3971916c1ea67dbf043c847632f316b33118dc3f6.jpeg';
const PROMO_BG = '/bodymax-promo.png';

const HIGHLIGHTS = [
  { id: '01', title: '2,100+ Exercises', desc: 'Master your form with high-quality video demos for every movement.' },
  { id: '02', title: 'Adaptive Progression', desc: 'Weights and reps auto-adjust based on your performance and fatigue.' },
  { id: '03', title: 'Smart Recovery', desc: 'Advanced fatigue and recovery tracking to prevent overtraining.' },
  { id: '04', title: 'Build Your Own', desc: 'Powerful custom program builder for advanced athletes.' },
  { id: '05', title: 'Smart Coach', desc: 'Real-time coaching chat to answer questions and adjust your plan.' },
  { id: '06', title: 'Cardio Integration', desc: 'Smart cardio blocks seamlessly woven into your strength routine.' },
  { id: '07', title: 'RPE Tracking', desc: 'Rate of Perceived Exertion logging for effort-based training.' },
  { id: '08', title: '7-Day Free Trial', desc: 'Experience elite training with zero commitment. No card required.' },
];

const FEATURES = [
  { icon: Brain, title: 'Personalized Programming', desc: 'Workouts tailored to your goals, available equipment, and fitness level.' },
  { icon: Target, title: 'Smart Exercise Selection', desc: 'Alternates muscle groups to optimize recovery and prevent overtraining.' },
  { icon: TrendingUp, title: 'Adaptive Progression', desc: 'Automatically adjusts weight and reps based on your performance logs.' },
  { icon: UserCheck, title: 'Coach Support', desc: 'Direct access to expert coaching aligned with your programming.' },
];

const STEPS = [
  { num: '01', title: 'Set Your Profile', desc: 'Input your stats, goals, and gym equipment. Takes 2 minutes.' },
  { num: '02', title: 'Get Your Workout', desc: 'Receive a personalized plan generated by the BODYMAX algorithm.' },
  { num: '03', title: 'Log & Grow', desc: 'Track your sets. We handle the math for your next session.' },
];

const PRICING_FEATURES = [
  'Unlimited personalized workouts',
  'Adaptive progression engine',
  '2,400+ exercise video library',
  'Calorie tracker from real logged sets',
  'Cardio tracker with weekly targets',
  'Photograph a meal for instant macros',
  'Water & sleep logging',
  'Fatigue & recovery tracking',
  'Smart cardio integration',
  'Build your own program mode',
];

const MEDIA_NAMES = ["MEN'S HEALTH", 'GQ', 'THE VERGE', 'WIRED', 'CNET', 'MUSCLE & FITNESS', 'LIFEHACKER', "WOMEN'S HEALTH"];

// ═══ Section wrapper ═══
function Section({ id, children, className = '', bg = BG }) {
  return (
    <section id={id} className={`relative py-24 md:py-32 overflow-hidden ${className}`} style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-[1]">{children}</div>
    </section>
  );
}

function Overline({ children }) {
  return <p className="text-xs md:text-sm font-heading uppercase tracking-[0.2em] font-bold mb-4" style={{ color: LIGHT_BLUE }}>{children}</p>;
}

// ═══════════════════════════════════════════════════════════════
// LANDING PAGE
// ═══════════════════════════════════════════════════════════════

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [mobileNav, setMobileNav] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [pricingPlan, setPricingPlan] = useState('monthly');
  // Local-currency guide beside the euro price (euro is what Stripe actually charges).
  const { countries, country, setCountry, row: countryRow, guideFor } = useCountryPrices();
  const [showCountrySheet, setShowCountrySheet] = useState(false);
  const heroVideoMobileRef = useRef(null);
  const heroVideoDesktopRef = useRef(null);
  const [isDesktopHero, setIsDesktopHero] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);

  // Render only one hero block so the video is downloaded once
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = e => setIsDesktopHero(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Android users get the Play Store; everyone else keeps the sign-up flow.
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);


  // Counter animation hook
  const useCountUp = (target, duration = 1500) => {
    const [count, setCount] = useState(0);
    const [started, setStarted] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
      if (!ref.current) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !started) { setStarted(true); }
      }, { threshold: 0.2 });
      observer.observe(ref.current);
      return () => observer.disconnect();
    }, [started]);
    useEffect(() => {
      if (!started) return;
      const startTime = performance.now();
      const animate = () => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(eased * target));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, [started, target, duration]);
    return { count, ref };
  };

  const exerciseCount = useCountUp(2400);
  const userCount = useCountUp(500);

  useEffect(() => {
    if (user) navigate(user.onboarding_complete ? '/dashboard' : '/onboarding');
  }, [user, navigate]);

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Blog', href: '/blog' },
  ];

  const scrollTo = (id) => {
    setMobileNav(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 300);
  };

  const faqs = [
    { q: 'What makes BODYMAX different from other workout apps?', a: 'BODYMAX adapts to your actual performance — not just your goals. It logs what you really did, learns from repeated RPE signals, adjusts volume based on completed vs planned sets, and uses the same intelligence for both programming and coaching.' },
    { q: 'Does BODYMAX adapt my workouts over time?', a: 'Yes. BODYMAX tracks reps, weight, effort, and set completion across every session. It detects stalls, failures, successes, volume tolerance, and exercise fit. Calibration gating ensures changes are only made when enough evidence exists.' },
    { q: 'Can I change my workout duration?', a: 'Absolutely. You can adjust your available training time and BODYMAX will restructure your session to fit — whether you have 30 minutes or 75.' },
    { q: 'Does it work with home equipment?', a: 'Yes. During onboarding you specify your training environment. BODYMAX selects exercises for your setup — full gym, home gym, or bodyweight only.' },
    { q: 'Is BODYMAX suitable for beginners?', a: 'Yes. BODYMAX adapts to your fitness level and uses a calibration phase to understand your baseline. The system gates advanced adaptations behind minimum-evidence requirements.' },
    { q: 'Can I build my own program?', a: 'Yes. BODYMAX offers both coach-generated and custom program modes. Build your own by picking exercises from 2,400+ options — the brain still handles sets, reps, and progression.' },
  ];


  // Hero videos — some browsers/WebViews block autoplay, so keep retrying
  useEffect(() => {
    const vids = [heroVideoMobileRef.current, heroVideoDesktopRef.current].filter(Boolean);
    if (!vids.length) return;
    const tryPlay = () => vids.forEach(v => v.play().catch(() => {}));
    tryPlay();
    vids.forEach(v => v.addEventListener('loadeddata', tryPlay));
    const onInteract = () => { tryPlay(); document.removeEventListener('touchstart', onInteract); };
    document.addEventListener('touchstart', onInteract, { once: true, passive: true });
    return () => {
      vids.forEach(v => v.removeEventListener('loadeddata', tryPlay));
      document.removeEventListener('touchstart', onInteract);
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: BG, color: '#fff' }} data-testid="landing-page">
      <Helmet>
        <title>BODYMAX | Personal Trainer App — Built Around You</title>
        <meta name="description" content="Your personal trainer app. BODYMAX adapts to your goals, learns from real performance, and builds smarter programs every session." />
        <meta property="og:title" content="BODYMAX.FIT — Personal Training, Evolved" />
        <meta property="og:description" content="Adaptive workout programming that learns from every rep. From €9.99/month." />
        <meta property="og:image" content="https://bodymax.fit/og-image.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://bodymax.fit/" />
      </Helmet>

      {/* ═══ NAVIGATION ═══ */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(10,10,10,0.7)', borderColor: 'rgba(255,255,255,0.05)' }} data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <Link to="/" className="shrink-0"><span className="font-heading text-xl sm:text-2xl md:text-3xl font-black tracking-tight text-white">BODYMAX<span style={{ color: CTA_BLUE }}>.FIT</span></span></Link>
          
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(l => (
              l.href.startsWith('/') ? (
                <Link key={l.href} to={l.href} className="text-sm font-medium hover:text-white transition-colors" style={{ color: TEXT_SEC }}>
                  {l.label}
                </Link>
              ) : (
                <button key={l.href} onClick={() => scrollTo(l.href.slice(1))} className="text-sm font-medium hover:text-white transition-colors" style={{ color: TEXT_SEC }}>
                  {l.label}
                </button>
              )
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden md:inline-block text-sm font-medium hover:text-white transition-colors" style={{ color: TEXT_SEC }}>Sign In</Link>
            {isAndroid ? (
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-cta-play">
                <button className="px-4 py-2 sm:px-6 sm:py-2.5 font-heading uppercase font-bold text-xs sm:text-sm text-white tracking-wider active:scale-95 transition-transform rounded-lg" style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }} data-testid="nav-cta">
                  Get App
                </button>
              </a>
            ) : (
              <Link to="/register">
                <button className="px-4 py-2 sm:px-6 sm:py-2.5 font-heading uppercase font-bold text-xs sm:text-sm text-white tracking-wider active:scale-95 transition-transform rounded-lg" style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }} data-testid="nav-cta">
                  Get App
                </button>
              </Link>
            )}
            <button className="md:hidden" onClick={() => setMobileNav(!mobileNav)}><Menu size={24} /></button>
          </div>
        </div>
      </nav>

      {/* Mobile nav dropdown */}
      <AnimatePresence>
        {mobileNav && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="fixed inset-0 z-40 pt-16 overflow-y-auto" style={{ background: BG }}>
            <div className="flex flex-col items-center gap-6 pt-12">
              {navLinks.map(l => (
                l.href.startsWith('/') ? (
                  <Link key={l.href} to={l.href} onClick={() => setMobileNav(false)} className="text-2xl font-heading uppercase font-bold">{l.label}</Link>
                ) : (
                  <button key={l.href} onClick={() => scrollTo(l.href.slice(1))} className="text-2xl font-heading uppercase font-bold">{l.label}</button>
                )
              ))}
              <Link to="/login" className="text-lg" style={{ color: TEXT_SEC }}>Sign In</Link>
              <Link to="/register">
                <button className="px-8 py-3 font-heading uppercase font-bold text-white mt-4 rounded-xl" style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }}>Get Started</button>
              </Link>
              
              {/* Footer links in mobile menu */}
              <div className="mt-8 pt-6 border-t border-white/10 flex flex-col items-center gap-4 w-full px-8">
                <div className="flex items-center gap-4">
                  <a href="https://www.instagram.com/bodymaxfit/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E1306C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="#E1306C" stroke="none"/></svg>
                  </a>
                  <a href="https://www.tiktok.com/@bodymax.fit" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.52a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.67a8.2 8.2 0 004.76 1.52V6.76a4.83 4.83 0 01-1-.07z"/></svg>
                  </a>
                </div>
                <div className="flex flex-wrap justify-center gap-4">
                  {[
                    { label: 'Privacy', href: '/privacy' },
                    { label: 'Terms', href: '/terms' },
                    { label: 'Support', href: '/support' },
                  ].map(l => (
                    <a key={l.href} href={l.href} className="text-sm transition-colors" style={{ color: TEXT_SEC }}>{l.label}</a>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={() => setMobileNav(false)} className="absolute top-5 right-6"><X size={28} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden" style={{ background: BG }} data-testid="hero-section">
        {/* Gradient orbs */}
        
        {/* Parallax floating equipment — visible on all devices */}
        
        {/* MOBILE: Video banner on top, text scrolls over bottom of video */}
        {!isDesktopHero && (
        <div className="md:hidden">
          <div className="relative pt-20">
            <div className="relative w-full" style={{ height: 'min(56.25vw, 60vh)', marginBottom: '-80px' }}>
              <img src="/welcome-video-hdr-poster.webp" alt="" className="absolute inset-0 w-full h-full object-cover object-top" fetchpriority="high" />
              <video
                data-testid="landing-hero-video-mobile"
                ref={heroVideoMobileRef}
                poster="/welcome-video-hdr-poster.webp"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover object-top"
              >
                <source src="/welcome-video-hdr.mp4" type="video/mp4" />
                <source src="/welcome-video-hdr.webm" type="video/webm" />
              </video>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-32" style={{ background: `linear-gradient(to top, ${BG}, transparent)` }} />
          </div>
          <div className="relative z-10 px-6 pb-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Overline>Personal Trainer In Your Pocket</Overline>
              <h1 className="font-heading text-5xl uppercase tracking-tighter font-black leading-[0.9] mb-6">
                Think Less.<br /><span style={{ color: CTA_BLUE }}>Exercise More.</span>
              </h1>
              <p className="text-xl font-bold leading-relaxed text-white/80 mb-6">
                Get a personalized, adaptable workout plan that evolves with your progress.
              </p>
            </motion.div>
          </div>
        </div>
        )}

        {/* DESKTOP: Full background video with left-aligned text */}
        {isDesktopHero && (
        <div className="hidden md:block relative min-h-[85vh]">
          <div className="absolute inset-0 overflow-hidden">
            {/* Source is only 854x480 — stretching it full-bleed looks soft, so the
                frame sits in a centered 16:9 box and the rest of the header is a
                blurred poster + dark gradient. */}
            <img
              src="/welcome-video-hdr-poster.webp"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover object-top scale-110 blur-2xl opacity-40"
            />
            <div className="absolute inset-0" style={{ background: 'rgba(10,10,10,0.55)' }} />
            <div className="absolute inset-0 flex items-center justify-center px-8">
              <video
                data-testid="landing-hero-video-desktop"
                ref={heroVideoDesktopRef}
                poster="/welcome-video-hdr-poster.webp"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-[560px] lg:w-[680px] max-w-full aspect-video object-contain rounded-xl opacity-90"
              >
                <source src="/welcome-video-hdr.mp4" type="video/mp4" />
                <source src="/welcome-video-hdr.webm" type="video/webm" />
              </video>
            </div>
            <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${BG} 30%, transparent 70%)` }} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-12 flex items-center min-h-[85vh]">
            <div className="max-w-2xl">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <Overline>Personal Trainer In Your Pocket</Overline>
                <h1 className="font-heading text-7xl lg:text-8xl uppercase tracking-tighter font-black leading-[0.9] mb-8">
                  Think Less.<br /><span style={{ color: CTA_BLUE }}>Exercise More.</span>
                </h1>
                <p className="text-xl leading-relaxed mb-10 max-w-md" style={{ color: TEXT_SEC }}>
                  Get a personalized, adaptable workout plan that evolves with your progress. Ditch the notepad and maximize your gains.
                </p>
                <div className="flex flex-row gap-3 mb-6">
                  <Link to="/register">
                    <button className="px-7 py-3 font-heading uppercase font-bold text-white text-base tracking-wider active:scale-95 transition-transform flex items-center gap-2" style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }} data-testid="hero-cta">
                      Start Now <ArrowRight size={18} />
                    </button>
                  </Link>
                  <button onClick={() => scrollTo('how')} className="px-7 py-3 border font-heading uppercase font-bold text-sm tracking-wider hover:bg-white/5 transition-colors" style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
                    See How It Works
                  </button>
                </div>
                <p className="text-sm" style={{ color: TEXT_SEC }}>7-day free trial · No credit card required</p>
              </motion.div>
            </div>
          </div>
        </div>
        )}
      </section>

      {/* ═══ READY TO CRUSH — after hero ═══ */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/promo-gym.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'rgba(10,10,10,0.7)' }} />
        </div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight font-black mb-4">Personal Trainer In Your Pocket</h2>
          <p className="text-lg md:text-xl font-bold mb-8" style={{ color: TEXT_SEC }}>Join athletes transforming their training with intelligent programming.</p>
          <Link to="/register">
            <button className="px-10 py-4 font-heading uppercase font-bold text-white text-lg tracking-wider active:scale-95 transition-transform lp-cta-pulse rounded-xl" style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }}>
              Start Free Trial <ArrowRight size={20} className="inline ml-2" />
            </button>
          </Link>
        </div>
      </section>

      {/* ═══ CALORIES & CARDIO TRACKERS (high on the page) ═══ */}
      <LandingTrackers />

      {/* ═══ PROGRAMME REEL (high on the page) ═══ */}
      <LandingPrograms />

      {/* ═══ FEATURES ═══ */}
      <Section id="features" className="hidden md:block">
        <div className="text-center mb-16">
          <Overline>Features</Overline>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight font-black mb-4" data-testid="built-for-progress-heading">Built for Progress</h2>
          <p className="text-lg md:text-xl font-bold max-w-2xl mx-auto" style={{ color: TEXT_SEC }}>Everything you need to train smarter, recover better, and grow stronger — in one app.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {[
            { icon: Target, title: 'Personalized Programming', desc: 'Workouts tailored to your goals, available equipment, and fitness level.', img: '/feature-programming.webp', testId: 'feature-card-personalized' },
            { icon: Target, title: 'Smart Exercise Selection', desc: 'Alternates muscle groups to optimize recovery and prevent overtraining.', img: '/feature-exercise.webp', testId: 'feature-card-exercise' },
            { icon: TrendingUp, title: 'Adaptive Progression', desc: 'Automatically adjusts weight and reps based on your performance logs.', img: '/feature-progression.webp', testId: 'feature-card-progression' },
            { icon: UserCheck, title: 'Coach Support', desc: 'Direct access to expert coaching aligned with your programming.', img: '/feature-coach.webp', testId: 'feature-card-coach' },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, x: i % 2 === 0 ? -60 : 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: i * 0.1, ease: 'easeOut' }}
              className="relative w-full h-[400px] md:h-[480px] rounded-2xl overflow-hidden group border transition-colors duration-500 cursor-default"
              style={{ borderColor: 'rgba(255,255,255,0.08)', background: SURFACE }}
              onMouseEnter={e => e.currentTarget.style.borderColor = `${ACCENT}60`}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'}
              data-testid={f.testId}
            >
              {/* Background image */}
              <img src={f.img} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" loading="lazy" />
              
              {/* Gradient overlay */}
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${BG} 15%, ${BG}b0 50%, transparent)` }} />
              
              {/* Content */}
              <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end">
                <h3 className="font-heading text-2xl md:text-3xl uppercase tracking-tight font-bold text-white mb-3">{f.title}</h3>
                <p className="text-base md:text-lg font-medium leading-relaxed max-w-md" style={{ color: '#d4d4d8' }}>{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ═══ FEATURE HIGHLIGHTS — desktop only ═══ */}
      <section className="relative hidden md:block py-24 md:py-32 overflow-hidden" style={{ background: SURFACE }} data-testid="highlights-section">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: ACCENT, opacity: 0.03, filter: 'blur(120px)' }} />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-[1]">
          <div className="text-center mb-16">
            <Overline>Inside the App</Overline>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight font-black mb-4">Designed to Perform</h2>
            <p className="text-lg md:text-xl font-bold max-w-xl mx-auto" style={{ color: TEXT_SEC }}>Eight things working behind every session you train.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
            {HIGHLIGHTS.map((f, i) => (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: (i % 4) * 0.08, duration: 0.5 }}
                className="group relative p-7 md:p-8 transition-colors duration-300"
                style={{ background: SURFACE }}
                onMouseEnter={e => e.currentTarget.style.background = '#131313'}
                onMouseLeave={e => e.currentTarget.style.background = SURFACE}
                data-testid={`highlight-${f.id}`}
              >
                {/* Oversized index, sits behind the copy */}
                <span
                  className="absolute top-4 right-5 font-heading font-black text-5xl md:text-6xl leading-none select-none pointer-events-none transition-opacity duration-300 group-hover:opacity-100"
                  style={{ color: ACCENT, opacity: 0.09 }}
                  aria-hidden="true"
                >
                  {f.id}
                </span>

                <span className="block w-8 h-0.5 mb-5 transition-all duration-300 group-hover:w-14" style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }} aria-hidden="true" />

                <h3 className="relative font-heading text-xl md:text-2xl uppercase tracking-tight font-bold mb-2.5 text-white pr-14">
                  {f.title}
                </h3>
                <p className="relative text-base font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
                  {f.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ═══ APP CAROUSELS ═══ */}
      <LandingAppCarousels />

      {/* ═══ HOW IT WORKS — desktop only ═══ */}
      <section id="how" className="relative hidden md:block py-24 md:py-32 overflow-hidden" style={{ background: BG }}>
        {/* Background image — same overlay style as hero */}
        <div className="absolute inset-0">
          <img src="/how-it-works-bg.webp" alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${BG}e0, ${BG}90 40%, ${BG}e0), linear-gradient(to right, ${BG}c0, transparent 60%)` }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-[1]">
          <div className="text-center mb-16">
            <Overline>How It Works</Overline>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight font-bold">Your Journey to Peak Performance</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              { num: '01', title: 'Set Your Profile', desc: 'Input your stats, goals, and gym equipment. Choose coach-built programming or build your own custom program.' },
              { num: '02', title: 'Get Your Workout', desc: 'Receive a personalized plan generated by the BODYMAX algorithm — or hand-pick exercises from 2,400+ options.' },
              { num: '03', title: 'Log & Grow', desc: 'Track your sets and effort. The brain handles progression, adapts volume, and evolves your program every week.' },
            ].map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative p-6 rounded-xl"
                style={{ background: `${BG}cc`, backdropFilter: 'blur(8px)' }}
              >
                <span className="font-heading text-5xl font-black select-none mb-2 block" style={{ color: ACCENT }}>{s.num}</span>
                <div className="relative z-10">
                  <h3 className="font-heading text-xl uppercase tracking-tight font-bold mb-3">{s.title}</h3>
                  <p className="text-base font-medium leading-relaxed" style={{ color: TEXT_SEC }}>{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ANIMATED STATS ═══ */}
      <section className="relative py-12 overflow-hidden" style={{ background: BG }} data-testid="landing-stats">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div ref={exerciseCount.ref}>
              <div className="font-heading text-3xl md:text-5xl font-black" style={{ color: ACCENT }}>{exerciseCount.count.toLocaleString()}+</div>
              <div className="text-xs md:text-sm font-heading uppercase tracking-wider mt-1" style={{ color: TEXT_SEC }}>Exercises</div>
            </div>
            <div ref={userCount.ref}>
              <div className="font-heading text-3xl md:text-5xl font-black" style={{ color: ACCENT }}>{userCount.count}+</div>
              <div className="text-xs md:text-sm font-heading uppercase tracking-wider mt-1" style={{ color: TEXT_SEC }}>Users</div>
            </div>
            <div>
              <div className="font-heading text-3xl md:text-5xl font-black" style={{ color: ACCENT }}>24/7</div>
              <div className="text-xs md:text-sm font-heading uppercase tracking-wider mt-1" style={{ color: TEXT_SEC }}>Coach</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PROMO BANNER ═══ */}
      <section className="relative py-32 overflow-hidden" data-testid="promo-section">
        <div className="absolute inset-0">
          <img src="/promo-gym.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'rgba(10,10,10,0.7)' }} />
        </div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight font-black mb-4">Ready to Crush Your Goals?</h2>
          <p className="text-lg md:text-xl font-bold mb-8" style={{ color: TEXT_SEC }}>Join athletes transforming their training with intelligent programming.</p>
          <Link to="/register">
            <button className="px-10 py-4 font-heading uppercase font-bold text-white text-lg tracking-wider active:scale-95 transition-transform" style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }} data-testid="promo-cta">
              Start Free Trial <ArrowRight size={20} className="inline ml-2" />
            </button>
          </Link>
        </div>
      </section>


      {/* ═══ PRICING ═══ */}
      <Section id="pricing">
        <div className="text-center mb-12">
          <Overline>Pricing</Overline>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight font-bold">Invest in Your Wellbeing</h2>
        </div>
        <div className="max-w-lg mx-auto">
          {/* Monthly / Yearly Toggle */}
          <div className="flex items-center justify-center gap-1 mb-8 p-1 rounded-xl" style={{ background: '#1A1A1A' }}>
            <button
              onClick={() => setPricingPlan('monthly')}
              className={`flex-1 py-3 rounded-lg text-sm font-heading font-bold uppercase tracking-wider transition-all ${
                pricingPlan === 'monthly' ? 'text-black' : 'text-white/50'
              }`}
              style={pricingPlan === 'monthly' ? { background: LIGHT_BLUE } : {}}
            >Monthly</button>
            <button
              onClick={() => setPricingPlan('yearly')}
              className={`flex-1 py-3 rounded-lg text-sm font-heading font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${
                pricingPlan === 'yearly' ? 'text-black' : 'text-white/50'
              }`}
              style={pricingPlan === 'yearly' ? { background: LIGHT_BLUE } : {}}
            >Yearly <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${pricingPlan === 'yearly' ? 'bg-black/20 text-black' : 'text-accent'}`} style={pricingPlan !== 'yearly' ? { background: `${LIGHT_BLUE}26` } : {}}>-15%</span></button>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-10 rounded-2xl border text-center"
            style={{ background: SURFACE, borderColor: `${ACCENT}50`, boxShadow: `0 0 40px rgba(0,150,252,0.1)` }}
            data-testid="pricing-card"
          >
            <p className="font-heading uppercase tracking-wider font-bold text-sm mb-2" style={{ color: LIGHT_BLUE }}>Pro Plan</p>
            
            {pricingPlan === 'monthly' ? (
              <div className="mb-1">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-heading text-6xl font-black">€9.99</span>
                  <span className="text-lg" style={{ color: TEXT_SEC }}>/ month</span>
                </div>
              </div>
            ) : (
              <div className="mb-1">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-heading text-6xl font-black">€99</span>
                  <span className="text-lg" style={{ color: TEXT_SEC }}>/ year</span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <span className="text-base line-through" style={{ color: TEXT_SEC }}>€120</span>
                  <span className="text-sm font-bold" style={{ color: LIGHT_BLUE }}>Save 15%</span>
                </div>
              </div>
            )}
            
            {(() => {
              const guide = guideFor(pricingPlan === 'monthly' ? 9.99 : 99, pricingPlan);
              return (
                <div className="mb-8" data-testid="pricing-local-line">
                  {countryRow ? (
                    <p className="text-sm" style={{ color: TEXT_SEC }}>
                      <span className="mr-1.5" aria-hidden="true">{flagFor(countryRow.code)}</span>
                      {guide
                        ? <>About <span className="font-bold text-white">{guide}</span> in {countryRow.name}</>
                        : <>Billed in euro in {countryRow.name}</>}
                    </p>
                  ) : (
                    <p className="text-sm" style={{ color: TEXT_SEC }}>Billed in euro, worldwide</p>
                  )}
                  <button
                    onClick={() => setShowCountrySheet(true)}
                    className="mt-1 text-xs underline underline-offset-2"
                    style={{ color: TEXT_SEC }}
                    data-testid="pricing-country-btn"
                  >
                    {countryRow ? 'Not your country?' : 'Choose your country'}
                  </button>
                </div>
              );
            })()}
            <div className="space-y-3 text-left mb-8">
              {PRICING_FEATURES.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Check size={16} style={{ color: ACCENT }} className="shrink-0" />
                  <span className="text-sm">{f}</span>
                </div>
              ))}
            </div>
            <Link to="/register" className="block" data-testid="pricing-cta">
              <img
                src="/btn-7days-free.webp"
                alt="7 Days Free"
                width={1000}
                height={333}
                loading="lazy"
                decoding="async"
                draggable={false}
                className="w-full md:max-w-[340px] md:mx-auto block h-auto select-none active:scale-[0.98] transition-transform"
              />
            </Link>
            <p className="text-xs mt-4" style={{ color: TEXT_SEC }}>Cancel anytime · No commitment</p>
          </motion.div>
        </div>
      </Section>

      {/* ═══ GET THE APP ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden" style={{ background: SURFACE }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-[1]">
          <div className="max-w-2xl mx-auto text-center">
            <Overline>Get the App</Overline>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight font-bold mb-4">Train Anywhere</h2>
            <p className="text-base md:text-lg mb-10" style={{ color: TEXT_SEC }}>
              {isAndroid
                ? 'Download BODYMAX from Google Play for the best experience with push notifications and offline access.'
                : 'Install BODYMAX on your device for a native app experience. No app store needed.'}
            </p>

            {/* PWA Install Button */}
            {isAndroid ? (
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-10 py-4 font-heading uppercase font-bold text-white text-lg tracking-wider active:scale-95 transition-transform mb-12"
                style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }}
                data-testid="pwa-install-cta"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893 2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198 2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658 16.802 8.99l-2.303 2.303L5.864 2.658z"/>
                </svg>
                Get on Google Play
              </a>
            ) : (
            <button
              onClick={() => {
                if (window.deferredPrompt) {
                  window.deferredPrompt.prompt();
                } else {
                  window.location.href = '/register';
                }
              }}
              className="inline-flex items-center gap-3 px-10 py-4 font-heading uppercase font-bold text-white text-lg tracking-wider active:scale-95 transition-transform mb-12"
              style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }}
              data-testid="pwa-install-cta"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Install Web App
            </button>
            )}

            {/* Store badges */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold mb-6" style={{ color: '#555' }}>Also available on</p>
              <div className="flex items-center justify-center gap-5">
                {/* Apple App Store badge */}
                <div className="flex flex-col items-center gap-2" data-testid="app-store-badge">
                  <div className="flex items-center gap-3 px-6 py-3.5 rounded-xl border-2 opacity-60" style={{ borderColor: '#333', background: '#111' }}>
                    <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-[10px] text-white/50 leading-none">Download on the</div>
                      <div className="text-lg font-semibold text-white leading-tight">App Store</div>
                    </div>
                  </div>
                  <span className="text-xs font-heading uppercase tracking-wider font-bold" style={{ color: LIGHT_BLUE }}>Coming Soon</span>
                </div>

                {/* Google Play badge */}
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2" data-testid="play-store-badge">
                  <div className="flex items-center gap-3 px-6 py-3.5 rounded-xl border-2 hover:border-accent/50 transition-colors" style={{ borderColor: '#333', background: '#111' }}>
                    <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
                      <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893 2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198 2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658 16.802 8.99l-2.303 2.303L5.864 2.658z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-[10px] text-white/50 leading-none">GET IT ON</div>
                      <div className="text-lg font-semibold text-white leading-tight">Google Play</div>
                    </div>
                  </div>
                  <span className="text-xs font-heading uppercase tracking-wider font-bold" style={{ color: LIGHT_BLUE }}>Available Now</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <Section id="faq" bg={SURFACE}>
        <div className="text-center mb-12">
          <Overline>FAQ</Overline>
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight font-bold">Common Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, i) => (
            <div key={i} className="border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between py-5 text-left"
                data-testid={`faq-${i}`}
              >
                <span className="font-medium text-base pr-4">{faq.q}</span>
                <ChevronDown size={18} className={`shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} style={{ color: TEXT_SEC }} />
              </button>
              <AnimatePresence>
                {openFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <p className="pb-5 text-sm leading-relaxed" style={{ color: TEXT_SEC }}>{faq.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </Section>

      {/* ═══ FINAL CTA ═══ */}
      <Section>
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight font-black mb-4">Start Training Smarter</h2>
          <p className="text-base mb-8" style={{ color: TEXT_SEC }}>Your first workout is free. No credit card required.</p>
          <Link to="/register">
            <button className="px-12 py-4 font-heading uppercase font-bold text-white text-lg tracking-wider active:scale-95 transition-transform" style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }} data-testid="final-cta">
              Get Started Free
            </button>
          </Link>
        </div>
      </Section>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-12 pb-28 md:pb-12 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <span className="font-heading text-sm uppercase tracking-wider font-bold" style={{ color: TEXT_SEC }}>BODYMAX © {new Date().getFullYear()}</span>
            <a href="https://www.instagram.com/bodymaxfit/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors" aria-label="Instagram">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E1306C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="#E1306C" stroke="none"/></svg>
            </a>
            <a href="https://www.tiktok.com/@bodymax.fit" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors" aria-label="TikTok">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.52a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.67a8.2 8.2 0 004.76 1.52V6.76a4.83 4.83 0 01-1-.07z"/></svg>
            </a>
          </div>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            {[
              { label: 'Privacy', href: '/privacy' },
              { label: 'Terms', href: '/terms' },
              { label: 'Blog', href: '/blog' },
              { label: 'Support', href: '/support' },
              { label: 'Sitemap', href: '/sitemap.xml' },
            ].map(l => (
              <a key={l.href} href={l.href} className="text-sm hover:text-white transition-colors" style={{ color: TEXT_SEC }}>{l.label}</a>
            ))}
          </div>
        </div>
      </footer>

      <CountrySheet
        open={showCountrySheet}
        onClose={() => setShowCountrySheet(false)}
        countries={countries}
        selected={country}
        onSelect={setCountry}
      />
      </div>
  );
}
